import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useUser } from '../context/UserContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import CardAdmin from '../components/UI/CardAdmin';
import Table from '../components/UI/Table';
import actionLabels from '../config/actionLabels';

const DepartmentManagementPage = () => {
    const { user, logout } = useUser();
    const [departments, setDepartments] = useState([]);
    const [usersByPosition, setUsersByPosition] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState(null);

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: {
            name: '',
            supervisors: []
        }
    });	

	useEffect(() => {
		fetchDepartments();
		fetchUsersByPosition();
		setLoading(false);
	}, []);

	useEffect(() => {
        if (showModal && !selectedDepartment) {
            reset({
                name: '',
                supervisors: []
            });
        }
    }, [showModal, selectedDepartment, reset]);
	
	const fetchDepartments = async () => {
		try {
			const res = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/departments`, {}, logout);
			const data = await res.json();
			setDepartments(data || []);
		} catch (err) {
			console.error("Ошибка при загрузке отделов:", err);
			setError("Ошибка загрузки отделов");
		}
	};
	
	const fetchUsersByPosition = async () => {
		try {
			const res = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/departments/users`, {}, logout);
			const data = await res.json();
			setUsersByPosition(data || {});
		} catch (err) {
			console.error("Ошибка при загрузке пользователей:", err);
			setError("Ошибка загрузки пользователей");
		}
	};
	

    const onSubmit = async (data) => {
        try {
            if (selectedDepartment) {
                await fetchWithAuth(`${process.env.REACT_APP_API_URL}/departments/${selectedDepartment._id}`, {
                    method: 'PUT',
                    body: JSON.stringify(data)
                }, logout);
            } else {
                await fetchWithAuth(`${process.env.REACT_APP_API_URL}/departments`, {
                    method: 'POST',
                    body: JSON.stringify(data)
                }, logout);
            }
            setShowModal(false);
			fetchDepartments()
            reset();
        } catch (err) {
            setError('Ошибка сохранения отдела');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Вы уверены, что хотите удалить этот отдел?')) {
            try {
                await fetchWithAuth(`${process.env.REACT_APP_API_URL}/departments/${id}`, {
                    method: 'DELETE'
                }, logout);
				fetchDepartments()
            } catch (err) {
                setError('Ошибка удаления отдела');
            }
        }
    };
	
    const handleEdit = (department) => {
        setSelectedDepartment(department);
        reset({
            name: department.name,
            supervisors: department.supervisors.map(s => s._id)
        });
        setShowModal(true);
    };

	const columns = [
		{ accessor: 'name', header: 'Название' },
		{
			accessor: 'supervisors',
			header: 'Руководители (Ответственные)',
			// render: (value) => value && value.map(s => `${s.lastName} ${s.fullName} ${s.firstName} (${s.shiftSchedule?.name || 'Нет смены'} )`).join(', ')
			render: (value) => Array.isArray(value)
		    ? value.map(s =>
		        `${s.lastName || ''} ${s.fullName || ''} ${s.firstName || ''} (${s.shiftSchedule?.name || 'Нет смены'})`
		    ).join(', ')
		    : '—'
		
		},
		{
			accessor: 'actions',
			header: 'Действия',
			render: (_, row) => (
				<div className="flex gap-2">
					<Button onClick={() => handleEdit(row)} variant="edit">Редактировать</Button>
					<Button onClick={() => handleDelete(row._id)} variant="danger">Удалить</Button>
				</div>
			)
		}
	];
	


    if (loading) return <div>Загрузка...</div>;
    if (error) return <div>{error}</div>;

    return (
        <div className="container mx-auto p-4">
            <CardAdmin title="Управление отделами">
                <div className="mb-4">
                    <Button onClick={() => {
                        setSelectedDepartment(null);
                        setShowModal(true);
                    }}>
                        Создать отдел
                    </Button>
                </div>

                <Table
                    columns={columns}
                    data={departments}
                />
            </CardAdmin>

            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={selectedDepartment ? 'Редактирование отдела' : 'Создание отдела'}
            >
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 text-gray-400">
                    <div>
                        <label className="block mb-2">Название отдела</label>
                        <input
                            type="text"
                            {...register('name', { required: 'Название обязательно' })}
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#b9bdc0]"
                        />
                        {errors.name && <span className="text-red-500">{errors.name.message}</span>}
                    </div>

                    <div>
                        <label className="block mb-2">Руководители</label>
                        <select
                            multiple
                            {...register('supervisors')}
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#b9bdc0] h-64"
                        >
                            {Object.entries(usersByPosition).map(([position, users]) => (
								<optgroup key={position} label={position}>
                                    {users.map(user => (
                                        <option key={user._id} value={user._id}>
                                           {user.lastName} {user.fullName} {user.firstName} ({user.shiftSchedule || 'Нет смены'})
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button type="button" onClick={() => setShowModal(false)} variant="secondary">
                            Отмена
                        </Button>
                        <Button type="submit">
                            {selectedDepartment ? 'Сохранить' : 'Создать'}
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default DepartmentManagementPage; 